import { ToastrHelper, showToast } from '$lib/helpers/toastrHelper';
import { fetchWithAuth } from '$lib/utils/http/httpUtil';
import { type BaseListResponse, type ErrorResponse, SortBy } from '$lib/models/types';
import { type CreateEntityRequest, type GetEntityResponse, type PutEntityRequest } from '$lib/models/entity/types';
import { extractErrorMessage } from '$lib/utils/error/errorUtils';
import type { State } from '@vincjo/datatables/remote';
import type { EntityPageState } from '../models/entity/types';
import { writable } from 'svelte/store';

export const uiState = writable<EntityPageState>('DataTable');
function createEntityStore() {
  const type: string = 'Entity';

  return {
    fetchForClient: async(clientId: number, state: State): Promise<BaseListResponse> => {
      const { pageNumber, rowsPerPage, sort, filters } = state;

      let url = `/api/client/${clientId}/entity`;

      const sortBy: string =
        sort?.direction?.toString() != undefined ? (sort?.direction?.toString().toLowerCase() === 'asc' ? SortBy.Ascending : SortBy.Descending) : SortBy.Descending;

      url += `?pageNumber=${pageNumber}&pageSize=${rowsPerPage}&sortBy=${sortBy}&orderBy=${sort?.orderBy}`;

      if (filters) {
        url += filters
          .map(({ filterBy, value }) => {
            return `&${filterBy}=${value}`;
          })
          .join('');
      }

      const response = await fetchWithAuth(url, {
        method: 'GET',
      });

      if (!response.ok) {
        const errorMessage = await extractErrorMessage(response, `Failed to fetch entities (HTTP ${response.status})`);
        showToast({
          title: 'Error',
          message: errorMessage,
          type: 'error'
        });
        return { total: 0, data: [] };
      }

      try {
        return await response.json();
      } catch (error) {
        showToast({
          title: 'Error',
          message: 'Failed to parse enity data',
          type: 'error'
        });
        return { total: 0, data: [] };
      }
    },
    fetchById: async (clientId: number, id: number): Promise<GetEntityResponse | ErrorResponse> => {
      const baseUrl = `/api/client/${clientId}/entity`
      const response = await fetchWithAuth(`${baseUrl}/${id}`, {
        method: 'GET',
      });

      if (response.status == 204) {
        const errorMessage = ToastrHelper.notFound(type);
        showToast({
          title: 'Error',
          message: errorMessage,
          type: 'error'
        });
        return {
          message: errorMessage,
          detail: errorMessage, // Add detail field for Problem Details compatibility
        };
      }

      if (!response.ok) {
        const errorMessage = await extractErrorMessage(response, `Failed to fetch ${type} (HTTP ${response.status})`);
        showToast({
          title: 'Error',
          message: errorMessage,
          type: 'error'
        });
        return {
          message: errorMessage,
          detail: errorMessage, // Add detail field for Problem Details compatibility
        };
      }

      try {
        return await response.json();
      } catch (error) {
        const errorMessage = ToastrHelper.errorRetrieving(type);
        showToast({
          title: 'Error',
          message: errorMessage,
          type: 'error'
        });
        return {
          message: errorMessage,
          detail: errorMessage, // Add detail field for Problem Details compatibility
        };
      }
    },
    update: async (request: PutEntityRequest): Promise<void> => {
      const baseUrl = `/api/client/${request.clientId}/entity`
      const response = await fetchWithAuth(`${baseUrl}/${request.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(request),
      });

      if (response.ok) {
        return;
      } else {
        throw await response.json();
      }
    },
    create: async (clientId: number, request: CreateEntityRequest): Promise<void> => {
      const response = await fetchWithAuth(`/api/client/${clientId}/entity`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(request),
      });

      if (!response.ok) {
        throw await response.json();
      }
    },
  };
}

export function setUIState(newState: EntityPageState) {
    uiState.set(newState);
}

export const entityId = writable<number | null>();

export function setEntityId(id: number) {
    entityId.set(id);
}

export const entityStore = createEntityStore();
